import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import useSize from 'ahooks/lib/useSize';

interface MobileContextValue {
  isMobile: boolean;
  isDesktop: boolean;
  screenWidth: number;
  screenHeight: number;
  isNavigationOpen: boolean;
  setIsNavigationOpen: (open: boolean) => void;
  toggleNavigation: () => void;
}

const MobileContext = createContext<MobileContextValue>({} as MobileContextValue);

// Mobile breakpoint definition
const MOBILE_BREAKPOINT = 1024;

export function MobileProvider({ children }: { children: ReactNode }) {
  const [isNavigationOpen, setIsNavigationOpen] = useState(false);

  // Use ahooks useSize to monitor window size changes
  const size = useSize(document.querySelector('body'));

  const screenWidth = size?.width || window.innerWidth;
  const screenHeight = size?.height || window.innerHeight;

  const isMobile = screenWidth < MOBILE_BREAKPOINT;
  const isDesktop = screenWidth >= MOBILE_BREAKPOINT;

  const toggleNavigation = () => {
    setIsNavigationOpen(!isNavigationOpen);
  };

  // Auto-close mobile navigation when screen size changes to desktop
  useEffect(() => {
    if (isDesktop && isNavigationOpen) {
      setIsNavigationOpen(false);
    }
  }, [isDesktop, isNavigationOpen]);

  const value: MobileContextValue = {
    isMobile,
    isDesktop,
    screenWidth,
    screenHeight,
    isNavigationOpen,
    setIsNavigationOpen,
    toggleNavigation,
  };

  return <MobileContext.Provider value={value}>{children}</MobileContext.Provider>;
}

export function useMobile() {
  const context = useContext(MobileContext);
  if (!context) {
    throw new Error('useMobile must be used within a MobileProvider');
  }
  return context;
}
